import { Box, MuiTable, TableBody, TableCell, TableHead, TableRow } from '@color/continuum';
import type { ReactNode } from 'react';

import { AccessionCell, GHgvsCell } from './ListView/columnCells';
import type { VariantConfirmation } from './types';

export interface SelectedConfirmationsTableExtensionColumn {
  /** Stable key for header and body cells */
  key: string;
  header: ReactNode;
  renderCell: (confirmation: VariantConfirmation) => ReactNode;
  width?: string;
}

export interface SelectedConfirmationsTableProps {
  confirmations: VariantConfirmation[];
  showAccessionColumn?: boolean;
  showSampleColumn?: boolean;
  showGhgvsColumn?: boolean;
  extensionColumns?: SelectedConfirmationsTableExtensionColumn[];
  /** When set, applied to every extension column body cell (e.g. align with selects vs tall rows). */
  extensionVerticalAlign?: 'top' | 'middle';
}

export function SelectedConfirmationsTable(props: SelectedConfirmationsTableProps) {
  const {
    confirmations,
    showAccessionColumn = true,
    showSampleColumn = true,
    showGhgvsColumn = true,
    extensionColumns,
    extensionVerticalAlign,
  } = props;

  const extensionBodyCellSx =
    extensionVerticalAlign != null ? { verticalAlign: extensionVerticalAlign } : undefined;

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <MuiTable size="small">
        <TableHead>
          <TableRow>
            {showAccessionColumn && <TableCell sx={{ width: '130px' }}>Accession #</TableCell>}
            {showSampleColumn && <TableCell sx={{ width: '200px' }}>Sample</TableCell>}
            {showGhgvsColumn && <TableCell sx={{ width: '260px' }}>gHGVS</TableCell>}
            {extensionColumns?.map((column) => (
              <TableCell
                key={column.key}
                sx={column.width != null ? { width: column.width } : undefined}
              >
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {confirmations.map((confirmation) => (
            <TableRow key={confirmation.id}>
              {showAccessionColumn && (
                <TableCell>
                  <AccessionCell confirmation={confirmation} asExternalLink />
                </TableCell>
              )}
              {showSampleColumn && <TableCell>{confirmation.sample.kit_barcode}</TableCell>}
              {showGhgvsColumn && (
                <TableCell>
                  <GHgvsCell confirmation={confirmation} />
                </TableCell>
              )}
              {extensionColumns?.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    ...(column.width != null ? { width: column.width } : {}),
                    ...extensionBodyCellSx,
                  }}
                >
                  {column.renderCell(confirmation)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </Box>
  );
}
